import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import translations, { Language, TranslationKeys, rtlLanguages } from '@/i18n/translations';
import { getLangFromPath, stripLangPrefix, SUPPORTED_LANGS, DEFAULT_LANG } from '@/hooks/useLocalizedPath';
import { getLocalizedPathForLang } from '@/data/legalNav';

type LanguageContextType = {
  lang: Language;
  setLang: (l: Language) => void;
  t: TranslationKeys;
  isRtl: boolean;
};

const LanguageContext = createContext<LanguageContextType | null>(null);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [lang, setLangState] = useState<Language>(() => {
    // First priority: URL path
    const urlLang = getLangFromPath(location.pathname);
    if (urlLang) return urlLang as Language;

    // Second: localStorage
    const saved = localStorage.getItem('salzburg-taxi-lang') as Language;
    if (saved && SUPPORTED_LANGS.includes(saved)) return saved;

    // Third: browser language
    const browserLang = navigator.language?.split('-')[0] as Language;
    if (SUPPORTED_LANGS.includes(browserLang)) return browserLang;

    return DEFAULT_LANG as Language;
  });

  const setLang = (newLang: Language) => {
    setLangState(newLang);
    navigate(getLocalizedPathForLang(location.pathname, newLang), { replace: true });
  };

  // Sync lang from URL changes
  useEffect(() => {
    const urlLang = getLangFromPath(location.pathname);
    if (urlLang && urlLang !== lang) {
      setLangState(urlLang as Language);
    }
  }, [location.pathname]);

  const t = translations[lang];
  const isRtl = rtlLanguages.includes(lang);

  useEffect(() => {
    localStorage.setItem('salzburg-taxi-lang', lang);
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang, isRtl]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isRtl }}>
      {children}
    </LanguageContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
